import { tv } from "tailwind-variants";

import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

export const accordionVariants = tv({
  slots: {
    group: ["w-full bg-transparent"], // container for all accordion items

    item: [
      "group relative bg-transparent", // accordion item wrapper
      "group/item", // named group for nested selectors
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60", // disabled state
    ],

    heading: ["m-0 p-0"], // remove default heading margins

    header: [
      // Layout
      "flex w-full items-center justify-between px-4",
      "cursor-pointer",
      // Remove native button styling
      "appearance-none",
      "!border-0 !border-t !border-solid !border-subtle-layer0", // only top border
      "!shadow-none !outline-none !ring-0",
      // Colors & transitions
      "transition-colors motion-reduce:transition-none",
      "bg-transparent text-primary",
      "hover:bg-control-hover",
      "active:bg-control-hover",
      // Pressed state (maintain top border only)
      "data-[pressed]:!border-0 data-[pressed]:!border-t data-[pressed]:!border-solid data-[pressed]:!border-subtle-layer0",
      "data-[pressed]:!bg-control-hover data-[pressed]:!shadow-none",
      "data-[pressed]:!outline-none data-[pressed]:!ring-0 data-[pressed]:!ring-offset-0",
      // Focus state (keyboard/tab navigation)
      "focus-visible:!ring-outline-primary focus-visible:!ring-2 focus-visible:!ring-inset focus-visible:!ring-offset-0",
      "focus-visible:!outline-none",
      // Disabled state
      "data-[disabled]:text-text-disabled data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed",
    ],

    title: [
      "truncate text-left font-semibold leading-none tracking-normal", // text styling & truncation
      "text-inherit", // inherit color from button
      "group-data-[disabled]:text-disabled", // disabled text color
    ],

    chevron: [
      "size-4 flex-shrink-0", // icon size, prevent shrinking
      "text-primary group-data-[disabled]:text-disabled", // icon color
      "transition-transform", // smooth rotation
      "group-data-[expanded]:-rotate-180", // rotate when expanded
    ],

    panel: [
      "bg-transparent px-4 pb-4 pt-1 text-sm text-primary", // content styling & spacing
      "data-[entering]:animate-in data-[entering]:fade-in data-[entering]:slide-in-from-top-1", // enter animation
      "data-[exiting]:animate-out data-[exiting]:fade-out data-[exiting]:slide-out-to-top-1", // exit animation
    ],
  },
  variants: {
    size: {
      small: { header: "h-7", title: "text-base", chevron: "size-4" }, // 28px height
      default: { header: "h-9", title: "text-base", chevron: "size-4" }, // 36px height
      large: { header: "h-11", title: "text-base", chevron: "size-4" }, // 44px height
    },
  },
  defaultVariants: { size: "default" },
});

export type AccordionVariantProps = VariantProps<typeof accordionVariants>;
export type AccordionSlots = ReturnType<typeof accordionVariants>;
export type AccordionClassNames = SlotsToClasses<keyof AccordionSlots>;
